import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CaregiverPatientList from '../components/CaregiverPatientList';
import CaregiverActionCenter from '../components/CaregiverActionCenter';
import CaregiverReportGenerator from '../components/CaregiverReportGenerator';
import EmergencyEventHistory from '../components/EmergencyEventHistory';

const { FiUsers, FiLayers, FiActivity, FiShield, FiFileText, FiBell, FiLogOut, FiMenu } = FiIcons;

const CaregiverDashboard = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [activeTab, setActiveTab] = useState('roster');

  const menuItems = [
    { id: 'roster', label: 'Patient Roster', icon: FiUsers },
    { id: 'reports', label: 'Shift Reports', icon: FiFileText },
    { id: 'history', label: 'Audit History', icon: FiLayers }
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex">
      {/* Sidebar */}
      <aside className="w-80 bg-white border-r border-gray-50 hidden lg:flex flex-col sticky top-0 h-screen z-30">
        <div className="p-10">
          <div className="flex items-center space-x-3 text-indigo-600 mb-12">
            <div className="w-10 h-10 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-100">
              <SafeIcon icon={FiShield} className="text-xl" />
            </div>
            <span className="text-2xl font-black tracking-tighter">iVivaCare</span>
          </div>
          <nav className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center space-x-3 px-6 py-4 rounded-[1.5rem] text-sm font-bold transition-all ${activeTab === item.id ? 'bg-gray-900 text-white shadow-xl shadow-gray-200' : 'text-gray-400 hover:bg-gray-50'}`}
              >
                <SafeIcon icon={item.icon} className="text-lg" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
        <div className="mt-auto p-10 border-t border-gray-50">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-gray-100 rounded-2xl overflow-hidden border-2 border-white shadow-md">
              <img src="https://ui-avatars.com/api/?name=Care+Giver&background=6366f1&color=fff" alt="Avatar" />
            </div>
            <div>
              <p className="text-sm font-black text-gray-900">Alex Rivera</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Social Worker</p>
            </div>
          </div>
          <button className="flex items-center space-x-2 text-gray-400 hover:text-red-500 font-black text-xs uppercase tracking-widest transition-colors">
            <SafeIcon icon={FiLogOut} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen overflow-x-hidden p-8 lg:p-16">
        <header className="mb-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h1 className="text-5xl font-black text-gray-900 tracking-tighter">Care Portal</h1>
            <p className="text-gray-400 mt-2 font-medium">Verified reporting and patient oversight.</p>
          </div>
          <button className="w-14 h-14 bg-white border border-gray-100 text-gray-400 rounded-2xl flex items-center justify-center hover:text-indigo-600 transition-colors shadow-sm">
            <SafeIcon icon={FiBell} className="text-xl" />
          </button>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-12">
          <div className="xl:col-span-1 border-r border-gray-50 pr-0 xl:pr-12">
            <CaregiverPatientList onSelectPatient={setSelectedPatient} />
          </div>

          <div className="xl:col-span-2">
            <AnimatePresence mode="wait">
              {selectedPatient ? (
                <motion.div
                  key={selectedPatient.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-12"
                >
                  <div className="bg-indigo-900 p-10 rounded-[3rem] text-white relative overflow-hidden">
                    <div className="relative z-10 flex justify-between items-start">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-300 mb-2">Active Session</p>
                        <h2 className="text-4xl font-black tracking-tight">{selectedPatient.name}</h2>
                      </div>
                      <div className="px-4 py-2 bg-white/10 rounded-xl border border-white/20 text-[10px] font-black uppercase tracking-widest">
                        {activeTab === 'reports' ? 'Reporting Mode' : 'Monitoring Mode'}
                      </div>
                    </div>
                  </div>

                  {activeTab === 'reports' ? (
                    <CaregiverReportGenerator patient={selectedPatient} />
                  ) : (
                    <>
                      <CaregiverActionCenter patient={selectedPatient} />
                      <EmergencyEventHistory />
                    </>
                  )}
                </motion.div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-20 bg-gray-50/50 rounded-[3rem] border-4 border-dashed border-gray-100">
                  <div className="w-24 h-24 bg-gray-100 text-gray-300 rounded-full flex items-center justify-center mb-8">
                    <SafeIcon icon={FiUsers} className="text-4xl" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900">Select a Patient</h3>
                  <p className="text-gray-400 max-w-sm mx-auto mt-4">Select a patient from the roster to begin {activeTab === 'reports' ? 'shift reporting' : 'monitoring'}.</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CaregiverDashboard;